import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import AppShell from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";

export default function TrainerDashboard() {
  const [clients, setClients] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [c, i] = await Promise.all([api.get("/clients"), api.get("/inquiries")]);
      setClients(c.data); setInquiries(i.data);
    } catch (e) { toast.error("Failed to load"); }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const createClient = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/clients", form);
      toast.success("Client created");
      setForm({ name: "", email: "", password: "" });
      setOpen(false);
      load();
    } catch (e2) {
      toast.error(formatApiError(e2.response?.data?.detail) || "Failed");
    }
    setSaving(false);
  };

  const markRead = async (id) => {
    try {
      await api.put(`/inquiries/${id}/read`);
      setInquiries((x) => x.map(i => i.id === id ? { ...i, read: true } : i));
    } catch {}
  };

  return (
    <AppShell
      title="Command Center"
      actions={
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-client-btn" className="rounded-none bg-[#FF3B30] hover:bg-[#D63028] text-white uppercase tracking-[0.2em] h-10 text-xs">+ New Client</Button>
          </DialogTrigger>
          <DialogContent className="rounded-none bg-[#0A0A0A] border-zinc-800 text-white">
            <DialogHeader><DialogTitle className="font-display text-3xl uppercase tracking-tight">Add Client</DialogTitle></DialogHeader>
            <form onSubmit={createClient} className="space-y-5">
              <Input data-testid="new-client-name" placeholder="Full name" value={form.name} onChange={e => setForm({...form, name: e.target.value})} required className="rounded-none bg-black border-zinc-800 h-12 text-white" />
              <Input data-testid="new-client-email" type="email" placeholder="Email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} required className="rounded-none bg-black border-zinc-800 h-12 text-white" />
              <Input data-testid="new-client-password" type="text" placeholder="Initial password (share securely)" value={form.password} onChange={e => setForm({...form, password: e.target.value})} required minLength={6} className="rounded-none bg-black border-zinc-800 h-12 text-white" />
              <DialogFooter>
                <Button data-testid="new-client-save" disabled={saving} type="submit" className="rounded-none bg-[#FF3B30] hover:bg-[#D63028] text-white uppercase tracking-[0.2em] h-11 text-xs w-full">
                  {saving ? "Creating..." : "Create Client Account"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      }
    >
      <Tabs defaultValue="clients" className="w-full">
        <TabsList className="rounded-none bg-transparent border-b border-zinc-800 w-full justify-start gap-8 p-0 h-12">
          <TabsTrigger value="clients" data-testid="tab-clients" className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#FF3B30] data-[state=active]:text-white data-[state=active]:bg-transparent data-[state=active]:shadow-none text-zinc-500 uppercase tracking-[0.2em] text-xs h-12 px-0">
            Clients ({clients.length})
          </TabsTrigger>
          <TabsTrigger value="inquiries" data-testid="tab-inquiries" className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#FF3B30] data-[state=active]:text-white data-[state=active]:bg-transparent data-[state=active]:shadow-none text-zinc-500 uppercase tracking-[0.2em] text-xs h-12 px-0">
            Inquiries ({inquiries.filter(i => !i.read).length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="clients" className="mt-8">
          {loading ? (
            <div className="text-zinc-500 text-xs uppercase tracking-[0.3em]">Loading...</div>
          ) : clients.length === 0 ? (
            <EmptyState title="No clients yet" body="Add your first client to start tracking check-ins, plans and progress." />
          ) : (
            <div className="border-t border-l border-zinc-800 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
              {clients.map((c) => (
                <Link key={c.id} to={`/app/clients/${c.id}`} data-testid={`client-card-${c.id}`}
                      className="grid-cell block group relative">
                  {c.unread_messages > 0 && (
                    <span data-testid={`client-unread-${c.id}`} className="absolute top-4 right-4 bg-[#FF3B30] text-white text-[10px] font-mono px-2 py-0.5">
                      {c.unread_messages} NEW
                    </span>
                  )}
                  <div className="font-mono text-xs text-[#FF3B30] mb-4">{(c.name || "?").slice(0, 2).toUpperCase()}</div>
                  <div className="font-display text-3xl uppercase group-hover:text-[#FF3B30] transition">{c.name}</div>
                  <div className="text-xs text-zinc-500 mt-1">{c.email}</div>
                  <div className="mt-6 flex gap-6 flex-wrap">
                    <Stat label="Last Weight" value={c.latest_weight ? `${c.latest_weight} lb` : "—"} />
                    <Stat label="Last Check-in" value={c.latest_checkin ? new Date(c.latest_checkin).toLocaleDateString() : "—"} />
                    <BillingStat billing={c.billing} testid={`client-billing-${c.id}`} />
                  </div>
                </Link>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="inquiries" className="mt-8">
          {inquiries.length === 0 ? (
            <EmptyState title="No inquiries yet" body="Inquiries submitted from your public landing page will appear here." />
          ) : (
            <div className="space-y-px bg-zinc-800 border border-zinc-800">
              {inquiries.map((i) => (
                <div key={i.id} data-testid={`inquiry-${i.id}`} className={`bg-[#0A0A0A] p-6 ${!i.read ? "border-l-2 border-l-[#FF3B30]" : ""}`}>
                  <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
                    <div>
                      <div className="font-display text-2xl uppercase">{i.name}</div>
                      <div className="text-xs text-zinc-500 mt-1">{i.email}{i.phone ? ` · ${i.phone}` : ""}</div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500">{new Date(i.created_at).toLocaleString()}</div>
                      {!i.read && <Button onClick={() => markRead(i.id)} size="sm" data-testid={`inquiry-read-${i.id}`} className="rounded-none bg-zinc-800 hover:bg-zinc-700 text-white text-[10px] uppercase tracking-[0.2em] h-8">Mark Read</Button>}
                    </div>
                  </div>
                  <p className="text-zinc-300 text-sm leading-relaxed whitespace-pre-wrap">{i.message}</p>
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </AppShell>
  );
}

const Stat = ({ label, value }) => (
  <div>
    <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-600">{label}</div>
    <div className="font-mono text-sm text-zinc-200 mt-1">{value}</div>
  </div>
);

const BILLING_STYLE = {
  active:   { color: "text-emerald-400", label: "Active" },
  paid:     { color: "text-emerald-400", label: "Paid" },
  past_due: { color: "text-[#FF3B30]",   label: "Past Due" },
  none:     { color: "text-zinc-600",    label: "No Plan" },
};

const BillingStat = ({ billing, testid }) => {
  const s = BILLING_STYLE[billing?.status || "none"] || BILLING_STYLE.none;
  return (
    <div data-testid={testid}>
      <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-600">Billing</div>
      <div className={`font-mono text-sm mt-1 ${s.color}`}>{s.label}</div>
    </div>
  );
};

const EmptyState = ({ title, body }) => (
  <div className="border border-dashed border-zinc-800 p-16 text-center">
    <div className="font-display text-3xl uppercase mb-3">{title}</div>
    <p className="text-zinc-500 text-sm">{body}</p>
  </div>
);
